"use client";

import { useState } from "react";
import {
  UploadStatus,
  ProcessingStatus,
} from "@/components/features/FormProvider";
import { getAttributeString } from "@/lib/utils/dynamodb";
import { useFormProcessing } from "@/contexts/FormProcessingContext";

export type FormValues = {
  file: FileList;
  email: string;
  customPrompt?: string;
  structureDefinitionId?: string;
};

export const useAudioProcessing = () => {
  const { startProcessing, completeProcessing, setFormState } =
    useFormProcessing();
  const [status, setStatus] = useState<UploadStatus>({
    state: "PENDING",
    message: "ファイルを選択してください",
  });
  const [audioUrl, setAudioUrl] = useState<string>("");
  const [fileName, setFileName] = useState<string>("");
  const [username, setUsername] = useState<string>("");
  const [timestamp, setTimestamp] = useState<string>("");
  const [processingStatus, setProcessingStatus] =
    useState<ProcessingStatus | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRebuilding, setIsRebuilding] = useState(false);

  // ファイル処理状態を確認する関数 (パラメータ付き)
  const checkProcessingStatusWithParams = async (
    employeeID: string,
    reportID: string
  ) => {
    try {
      console.log("使用するパラメータ:", { employeeID, reportID });

      if (!employeeID || !reportID) {
        console.error("必要なパラメータが不足しています:", {
          employeeID,
          reportID,
        });
        setStatus({
          state: "ERROR",
          message:
            "処理に必要な情報が不足しています。管理者に連絡してください。",
        });
        return;
      }

      console.log("Sending params:", { employeeID, reportID });

      // Next.js API Routesを使用（外部APIではなく、プロキシ経由）
      const apiEndpoint = `/api/get-report`;
      const url = `${apiEndpoint}?employeeID=${employeeID}&reportID=${reportID}`;

      // idTokenを取得してヘッダーに付与
      const headers: Record<string, string> = { "Content-Type": "application/json" };
      try {
        const { fetchAuthSession } = await import('aws-amplify/auth');
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken?.toString();
        if (idToken) {
          headers.Authorization = idToken;
          console.log('🔐 checkProcessingStatusWithParams - ID Token:', idToken.substring(0, 50) + '...');
          console.log('🔐 checkProcessingStatusWithParams - Authorizationヘッダー存在:', !!headers.Authorization);
          console.log('🔐 checkProcessingStatusWithParams - Authorizationヘッダー長さ:', headers.Authorization?.length || 0);
        }
      } catch (e) {
        console.warn("checkProcessingStatusWithParams: idToken取得失敗", e);
      }

      console.log('🔐 checkProcessingStatusWithParams - リクエストヘッダー:', headers);

      const response = await fetch(url, {
        method: "GET",
        headers,
      });

      console.log("🔐 checkProcessingStatusWithParams - レスポンスステータス:", response.status);
      console.log("🔐 checkProcessingStatusWithParams - レスポンスヘッダー:", Object.fromEntries(response.headers.entries()));

      if (response.ok) {
        const data = await response.json();
        // APIレスポンス形式をログに出力（デバッグ用）
        console.log("API Response:", data);

        // データに必要なプロパティがあるか確認
        if (!data || typeof data !== "object") {
          console.error("無効なレスポンス形式:", data);
          setStatus({
            state: "ERROR",
            message: "サーバーから無効なレスポンスを受信しました",
          });
          return;
        }

        // statesプロパティが存在することを確認
        const states = data.states || "unknown";

        // reportデータの処理 - 複数の形式に対応
        console.log("元のレポートデータ:", data.report);

        // データがItem直下に存在する場合もある（DynamoDB API直接呼び出し時など）
        let reportData = data.report || data.Item || {};

        console.log("レポートデータの詳細:", reportData);
        console.log(
          "レポートのキー:",
          reportData ? Object.keys(reportData) : "データなし"
        );

        // Lambda関数からのレスポンスをさらに詳しく調査
        if (reportData) {
          // 特定のフィールドのアクセス例
          if (reportData.pk) console.log("pk存在確認:", reportData.pk);
          if (reportData.meeting_name)
            console.log("meeting_name存在確認:", reportData.meeting_name);
        }

        // データがない、またはデータが空オブジェクトの場合
        if (
          !reportData ||
          (typeof reportData === "object" &&
            Object.keys(reportData).length === 0)
        ) {
          console.log("空のレポートデータを検出、テンプレートを使用します");
          // 空のオブジェクトの場合は、サンプルのJSONを使用
          reportData = {
            pk: { S: data.employeeID || employeeID },
            sk: { S: data.reportID || reportID },
            client: { S: "<UNKNOWN>" },
            datetime: { S: "<UNKNOWN>" },
            meeting_name: { S: "営業日報" },
            next_actions: { S: "次のアクションは未定です" },
            participants: { L: [{ S: "参加者情報なし" }] },
            result: { S: "処理結果は未確定です" },
            states: { S: states },
            summary: { S: "要約情報はまだ生成されていません" },
            timestamp: { S: new Date().toISOString() },
          };

          console.log("Empty report data, using template:", reportData);
        } else {
          console.log(
            "有効なレポートデータを検出:",
            Object.keys(reportData).length
          );

          // Lambda関数内で既にJSON文字列に変換されている可能性がある
          // JSONオブジェクトの状態で返される場合も考慮
          if (typeof reportData === "string") {
            try {
              reportData = JSON.parse(reportData);
              console.log("レポートデータ文字列からパース:", reportData);
            } catch (e) {
              console.error("レポートデータのパース失敗:", e);
            }
          }

          // DynamoDB形式のチェック
          const firstKey = Object.keys(reportData)[0];
          const firstValue = reportData[firstKey];
          const isPlainJson =
            firstKey &&
            (typeof firstValue === "string" ||
              Array.isArray(firstValue) ||
              (typeof firstValue === "object" &&
                !("S" in firstValue) &&
                !("L" in firstValue)));

          // メタデータ文字列のチェック（DynamoDB形式かどうか）
          const isStringField =
            reportData.meeting_name &&
            typeof reportData.meeting_name === "string";
          const isDynamoDbField =
            reportData.meeting_name &&
            typeof reportData.meeting_name === "object" &&
            reportData.meeting_name.S !== undefined;

          console.log("フィールド形式チェック:", {
            isPlainJson,
            isStringField,
            isDynamoDbField,
          });

          // 通常のJSON形式の場合はDynamoDB形式に変換
          if (isPlainJson || isStringField) {
            console.log("通常のJSONデータをDynamoDB形式に変換します");

            try {
              // ランタイムでデータ構造を確認し、データ形式に合わせて変換
              const convertedData: Record<string, unknown> = {};

              // オブジェクトの各フィールドを処理
              for (const key in reportData) {
                const value = reportData[key];
                // 現在のデータ型を確認
                if (typeof value === "string") {
                  // 文字列フィールドをDynamoDB形式に変換
                  convertedData[key] = { S: value };
                } else if (Array.isArray(value)) {
                  // 配列フィールドをDynamoDB形式に変換
                  convertedData[key] = {
                    L: value.map((item: unknown) => ({ S: String(item) })),
                  };
                } else if (value && typeof value === "object") {
                  // すでにオブジェクト形式の場合は、DynamoDB形式かどうかをさらにチェック
                  if ("S" in (value as Record<string, unknown>)) {
                    // 既にDynamoDB形式ならそのまま使用
                    convertedData[key] = value;
                  } else {
                    // その他のオブジェクトも文字列に変換
                    convertedData[key] = { S: JSON.stringify(value) };
                  }
                } else {
                  // その他の型も文字列に変換
                  convertedData[key] = { S: String(value || "") };
                }
              }

              reportData = convertedData;
              console.log("変換後のレポートデータ:", reportData);
            } catch (conversionError) {
              console.error("データ変換エラー:", conversionError);
            }
          } else if (!isDynamoDbField) {
            console.warn("不明なデータ形式です:", reportData);
          }
        }

        // ProcessingStatus形式に整形
        const processedData: ProcessingStatus = {
          employeeID: data.employeeID || employeeID,
          reportID: data.reportID || reportID,
          userId: data.userId || getAttributeString((reportData as Record<string, unknown>)?.pk) || employeeID,
          reportId: data.reportId || data.reportID || getAttributeString((reportData as Record<string, unknown>)?.sk) || reportID,
          structure_definition_id:
            (typeof data.structure_definition_id === 'string' ? data.structure_definition_id : undefined) ||
            getAttributeString((reportData as Record<string, unknown>)?.structure_definition_id),
          states: states,
          report: reportData,
        };

        // 処理したデータの確認ログ
        console.log("処理済みのデータ:", processedData);
        console.log("状態確認結果:", {
          states,
          employeeID: data.employeeID || employeeID,
          reportID: data.reportID || reportID,
          hasReportData: !!reportData,
          reportDataKeys: reportData ? Object.keys(reportData) : []
        });

        setProcessingStatus(processedData);

        // 処理状態によって表示を変更
        if (states === "reqCompleted") {
          // フォーム処理完了を通知
          completeProcessing();
          setStatus({
            state: "SUCCESS",
            message: "処理が完了しました！",
          });
        } else if (
          states &&
          typeof states === "string" &&
          states.startsWith("err")
        ) {
          setStatus({
            state: "ERROR",
            message: `エラーが発生しました: ${states}`,
          });
        } else {
          // 処理中の状態
          setStatus({
            state: "SUCCESS",
            message: `アップロード成功。現在の状態: ${states}`,
          });

          // まだ処理中なら5秒後に再確認
          setTimeout(() => {
            // 引き続き同じパラメータを使用
            checkProcessingStatusWithParams(employeeID, reportID);
          }, 5000);
        }
      } else {
        // レスポンスが失敗の場合、詳細な情報を取得
        const errorText = await response.text();
        console.error("API エラー:", response.status, response.statusText);
        console.error("エラーレスポンス:", errorText);
        
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch {
          errorData = { error: errorText || "不明なエラー" };
        }
        
        setStatus({
          state: "ERROR",
          message: `APIエラー (${response.status}): ${errorData.error || errorText}`,
        });
      }
    } catch (error) {
      console.error("状態確認エラー:", error);
      console.error("状態確認エラー詳細:", {
        employeeID,
        reportID,
        errorType: error?.constructor?.name,
        errorMessage: error instanceof Error ? error.message : String(error),
        errorStack: error instanceof Error ? error.stack : undefined
      });
      
      // より詳細なエラー情報を提供
      let errorMessage = "不明なエラーが発生しました";
      
      if (error instanceof Error) {
        errorMessage = error.message;
        
        // ネットワークエラーの場合の特別処理
        if (error.message.includes("Failed to fetch")) {
          errorMessage = "ネットワークエラー: API接続に失敗しました。インターネット接続を確認してください。";
        }
      }
      
      setStatus({
        state: "ERROR",
        message: errorMessage,
      });
    }
  };

  // 保存されたstateを使う元の関数（互換性のために残す）
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const checkProcessingStatus = async () => {
    const employeeID = username;
    const reportID = timestamp;

    console.log("保存されている値:", { username, timestamp });
    checkProcessingStatusWithParams(employeeID, reportID);
  };

  // レポートを再生成する関数
  const rebuildReport = async (
    employeeID: string,
    reportID: string,
    customPrompt: string
  ) => {
    try {
      setIsRebuilding(true);
      setStatus({
        state: "SAVING",
        message: "レポートを再生成中...",
      });

      // Next.js API Routesを使用（外部APIではなく、プロキシ経由）
      const apiEndpoint = `/api/rebuild-report`;
      console.log("再生成APIエンドポイント:", apiEndpoint);

      // 認証ヘッダーを取得
      const getAuthHeaders = async (): Promise<Record<string, string>> => {
        try {
          const { fetchAuthSession } = await import('aws-amplify/auth');
          const session = await fetchAuthSession();
          const idToken = session.tokens?.idToken?.toString();

          if (idToken) {
            console.log('🔐 rebuildReport - ID Token found:', idToken.substring(0, 50) + '...');
            return {
              "Content-Type": "application/json",
              Authorization: idToken,
            };
          }

          console.log('⚠️ rebuildReport - No ID Token found');
          return {
            "Content-Type": "application/json",
          };
        } catch (error) {
          console.warn("rebuildReport: 認証トークンの取得に失敗しました:", error);
          return {
            "Content-Type": "application/json",
          };
        }
      };

      const headers = await getAuthHeaders();
      console.log('🔐 rebuildReport - Request headers:', headers);

      const response = await fetch(apiEndpoint, {
        method: "POST",
        headers,
        body: JSON.stringify({
          employeeID,
          reportID,
          custom_prompt: customPrompt,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("API応答エラー:", response.status, errorText);
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch {
          errorData = { error: errorText || "不明なエラー" };
        }
        throw new Error(errorData.error || "レポートの再生成に失敗しました");
      }

      // 再生成リクエストが成功したら処理状態の確認を開始
      setStatus({
        state: "SUCCESS",
        message: "再生成リクエストを送信しました。処理中...",
      });

      // 複数回の状態確認を実行（再生成処理は時間がかかるため）
      const checkStatusMultipleTimes = async () => {
        console.log("再生成後の状態確認を開始:", { employeeID, reportID });
        
        // 初回確認（3秒後）
        setTimeout(async () => {
          console.log("再生成後 - 初回状態確認");
          await checkProcessingStatusWithParams(employeeID, reportID);
        }, 3000);
        
        // 2回目確認（8秒後）
        setTimeout(async () => {
          console.log("再生成後 - 2回目状態確認");
          await checkProcessingStatusWithParams(employeeID, reportID);
        }, 8000);
        
        // 3回目確認（15秒後）
        setTimeout(async () => {
          console.log("再生成後 - 3回目状態確認");
          await checkProcessingStatusWithParams(employeeID, reportID);
        }, 15000);
        
        // 4回目確認（25秒後）
        setTimeout(async () => {
          console.log("再生成後 - 4回目状態確認");
          await checkProcessingStatusWithParams(employeeID, reportID);
        }, 25000);
      };
      
      checkStatusMultipleTimes();
    } catch (error) {
      console.error("レポート再生成エラー:", error);
      setStatus({
        state: "ERROR",
        message:
          error instanceof Error
            ? error.message
            : "レポート再生成中にエラーが発生しました",
      });
    } finally {
      setIsRebuilding(false);
    }
  };

  const onSubmit = async (data: FormValues) => {
    console.log("useAudioProcessing onSubmit受信data:", data);
    if (!data.file || !data.file[0]) {
      setStatus({
        state: "ERROR",
        message: "ファイルを選択してください",
      });
      return;
    }

    if (!data.email || !data.email.includes("@")) {
      setStatus({
        state: "ERROR",
        message: "有効なメールアドレスを入力してください",
      });
      return;
    }

    try {
      setIsSubmitting(true);
      // フォーム処理開始を通知
      startProcessing();
      setFormState({ processingStage: "submitting" });

      setStatus({
        state: "SAVING",
        message: "アップロード中...",
      });

      const file = data.file[0];

      // ファイル拡張子を取得
      const extension = file.name.split(".").pop()?.toLowerCase();

      if (
        !extension ||
        !["wav", "mp3", "m4a", "mp4", "ogg", "webm"].includes(extension)
      ) {
        setStatus({
          state: "ERROR",
          message:
            "対応していないファイル形式です。WAV, MP3, M4A, MP4, OGG, WEBMのいずれかを選択してください。",
        });
        setIsSubmitting(false);
        return;
      }

      // APIエンドポイントURLを正しく構築
      const apiEndpoint = "/api/presigned-url";

      // structureDefinitionIdをローカルストレージに保存
      if (data.structureDefinitionId) {
        localStorage.setItem('lastUsedStructureDefinitionId', data.structureDefinitionId);
      }

      // 認証ヘッダーを取得
      const getAuthHeaders = async (): Promise<Record<string, string>> => {
        try {
          const { fetchAuthSession } = await import('aws-amplify/auth');
          const session = await fetchAuthSession();
          const idToken = session.tokens?.idToken?.toString();

          if (idToken) {
            console.log('🔐 Frontend - ID Token found:', idToken.substring(0, 50) + '...');
            return {
              "Content-Type": "application/json",
              Authorization: idToken,
            };
          }

          console.log('⚠️ Frontend - No ID Token found');
          return {
            "Content-Type": "application/json",
          };
        } catch (error) {
          console.warn("認証トークンの取得に失敗しました:", error);
          return {
            "Content-Type": "application/json",
          };
        }
      };

      // PresignedURLの取得リクエスト
      const headers = await getAuthHeaders();
      console.log('🔐 Frontend - Request headers:', headers);
      console.log('🔐 Frontend - API endpoint:', apiEndpoint);
      console.log('🔐 Frontend - Current location:', window.location.href);
      
      const apiResponse = await fetch(apiEndpoint, {
        method: "POST",
        headers,
        body: JSON.stringify({
          extension,
          user_email: data.email,
          customPrompt: data.customPrompt || "",
          structureDefinitionId: data.structureDefinitionId || "",
          fileName: file.name, // 音声ファイル名を送信
        }),
      });

      // APIレスポンスの詳細ログ（デバッグ用）
      console.log("API Response Status:", apiResponse.status);
      const responseText = await apiResponse.text();

      try {
        // JSONとしてパースできるかチェック
        const responseData = JSON.parse(responseText);
        console.log("API Response Data:", responseData);

        if (!apiResponse.ok) {
          throw new Error(
            responseData.error || "Presigned URLの取得に失敗しました"
          );
        }

        // APIレスポンスからデータを取得
        const { presigned_url, file_name, content_type } = responseData;

        // APIレスポンスからのデータ取得をデバッグ
        console.log("レスポンスデータからの取得:", {
          presigned_url: presigned_url || "未取得",
          file_name: file_name || "未取得",
          content_type: content_type || "未取得"
        });

        // presigned_urlとcontent_typeの存在チェック
        if (!presigned_url) {
          throw new Error("presigned_urlが取得できませんでした");
        }
        if (!content_type) {
          throw new Error("content_typeが取得できませんでした");
        }

        // 詳細なデバッグのためにすべてのフィールドを出力
        console.log("全レスポンスフィールド:", Object.keys(responseData));

        // usernameとtimestampを抽出
        const responseUsername = responseData.username;
        const responseTimestamp = responseData.timestamp;

        console.log("抽出された値:", { responseUsername, responseTimestamp });

        // usernameとtimestampを保存（ファイル名から抽出したフォールバック値も設定）
        const parsedUsername =
          responseUsername ||
          getFileParts(file_name).username ||
          data.email.split("@")[0];
        const parsedTimestamp =
          responseTimestamp || getFileParts(file_name).timestamp;

        console.log("保存する値:", { parsedUsername, parsedTimestamp });

        // 状態を更新
        setUsername(parsedUsername);
        setTimestamp(parsedTimestamp);
        setFileName(file_name);

        console.log("S3アップロード開始:", { presigned_url, content_type });

        // S3にファイルをアップロード
        const uploadResponse = await fetch(presigned_url, {
          method: "PUT",
          body: file,
          headers: {
            "Content-Type": content_type,
          },
        });

        console.log("S3アップロード完了:", uploadResponse.status);

        if (!uploadResponse.ok) {
          throw new Error("ファイルのアップロードに失敗しました");
        }

        // 🔄 メタデータをlocalStorageに保存（ReportHeaderTags用）
        try {
          const reportMetadata = {
            structureDefinitionId: data.structureDefinitionId || '',
            customPrompt: data.customPrompt || '',
            timestamp: Date.now(),
            employeeID: parsedUsername,
            reportID: parsedTimestamp
          };
          
          const metadataKey = `report-metadata-${parsedUsername}-${parsedTimestamp}`;
          localStorage.setItem(metadataKey, JSON.stringify(reportMetadata));
          console.log('🔄 [useAudioProcessing] メタデータをlocalStorageに保存:', {
            key: metadataKey,
            data: reportMetadata
          });
        } catch (metadataError) {
          console.error('🔄 [useAudioProcessing] メタデータ保存に失敗:', metadataError);
          // メタデータ保存失敗は致命的ではないので、処理は継続
        }

        // 成功メッセージとS3のURL
        const s3Url = presigned_url.split("?")[0];
        setAudioUrl(s3Url);
        setStatus({
          state: "SUCCESS",
          message: "アップロード成功。処理を開始します...",
        });

        console.log("処理状態確認開始:", { parsedUsername, parsedTimestamp });

        // アップロード後、処理状態の確認を開始 - ローカル変数を直接使用
        setTimeout(() => {
          // setStateの非同期更新を待たずに、確実な値を使って呼び出す
          checkProcessingStatusWithParams(parsedUsername, parsedTimestamp);
        }, 100);
      } catch (jsonError) {
        console.error("JSON Parse Error:", jsonError);
        setStatus({
          state: "ERROR",
          message: `API応答の解析に失敗しました: ${responseText.substring(
            0,
            100
          )}...`,
        });
      }
    } catch (error) {
      console.error("アップロードエラー:", error);
      setStatus({
        state: "ERROR",
        message:
          error instanceof Error
            ? error.message
            : "アップロード中にエラーが発生しました",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // ファイル名からusernameとtimestampを抽出する補助関数
  const getFileParts = (fileName: string) => {
    // ファイル名の例: "m-yamashita-20250502075757.mp3"
    const parts = fileName.split("-");
    let username = "";
    let timestamp = "";

    if (parts.length >= 2) {
      // 最後の部分は拡張子を含むタイムスタンプ
      const lastPart = parts[parts.length - 1];
      const timeWithExt = lastPart.split(".");
      timestamp = timeWithExt[0];

      // 最初からn-1部分はユーザー名
      username = parts.slice(0, -1).join("-");
    }

    return { username, timestamp };
  };

  return {
    status,
    audioUrl,
    fileName,
    processingStatus,
    isSubmitting,
    isRebuilding,
    onSubmit,
    rebuildReport,
  };
};
